import React from 'react';
import { TextAnalysisResult } from '@types';
import { md, Section, RiskFactorBars } from './ReportShared';
import UnifiedMap from '../UnifiedMap';

interface TextResultContentProps {
    result: TextAnalysisResult;
    isLoaded?: boolean; // For Google Maps
}

const TextResultContent: React.FC<TextResultContentProps> = ({ result, isLoaded }) => {
    const extractMapData = (content: string) => {
        const jsonBlockRegex = /```json\s*(\[\s*\{.*?\}\s*\])\s*```/s;
        const match = content.match(jsonBlockRegex);
        if (match) {
            try {
                const markers = JSON.parse(match[1]);
                const validMarkers = markers.filter((m: any) => typeof m.lat === 'number' && typeof m.lng === 'number');
                const cleanedContent = content.replace(match[0], '');
                return { markers: validMarkers, cleanedContent };
            } catch (e) {
                console.warn("Failed to parse embedded map data in report:", e);
            }
        }
        return { markers: [], cleanedContent: content };
    };

    return (
        <div className="space-y-8">
            {/* Automated Analysis Disclaimer */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start space-x-3">
                <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div className="flex-grow">
                    <p className="text-sm text-blue-800 font-bold">Project Intelligence Sync Active</p>
                    <p className="text-sm text-blue-700">
                        The background daily analysis will automatically update the active SRM project risk profile it is associated with.
                    </p>
                </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                <h3 className="font-bold text-slate-800 text-sm uppercase mb-2 border-b border-slate-200 pb-1">Executive Summary</h3>
                <div 
                    className="prose prose-sm text-slate-700 max-w-none" 
                    dangerouslySetInnerHTML={{ __html: md.render(result.summary || 'No summary provided.') }} 
                />
            </div>

            {result.riskFactors && result.riskFactors.length > 0 && (
                <Section title="Risk Factor Analysis">
                    <div className="h-64 w-full">
                        <RiskFactorBars data={result.riskFactors} />
                    </div>
                </Section>
            )}

            <div className="space-y-8">
                {(result.additionalSections || []).map((section, index) => {
                    const { markers, cleanedContent } = extractMapData(section.content || '');
                    
                    const mapCenter = markers.length > 0 ? { lat: markers[0].lat, lng: markers[0].lng } : undefined;
                    const mapZoom = markers.length > 0 ? 10 : 2;

                    const mapMarkers = markers.map((m: any, i: number) => ({
                         ...m,
                         id: `map-marker-${index}-${i}`,
                         lat: m.lat,
                         lng: m.lng,
                         name: m.name,
                         description: m.description || m.type,
                         color: '#3b82f6',
                         type: m.type || 'Location'
                    }));

                    return (
                        <Section key={index} title={section.heading || section.title || 'Intelligence Section'}>
                            {mapMarkers.length > 0 && (
                                <div className="mb-6 w-full h-[400px] rounded-lg overflow-hidden border border-slate-300 relative shadow-sm">
                                    <div className="absolute top-2 left-2 z-10 bg-white/90 backdrop-blur px-2 py-1 rounded text-xs font-bold text-slate-600 shadow-sm">
                                        GEOSPATIAL VISUALIZATION // {(section.heading || section.title || 'Section').toUpperCase()}
                                    </div>
                                    <UnifiedMap
                                        mapTitle="Geospatial Intelligence"
                                        isLoaded={isLoaded || false}
                                        choroplethEnabled={!!(result as any).globalRiskData} 
                                        initialViewState={{ 
                                            longitude: mapCenter?.lng || 0, 
                                            latitude: mapCenter?.lat || 0, 
                                            zoom: mapZoom || 2 
                                        }}
                                        theme="light"
                                        markers={mapMarkers || []}
                                        riskData={(result as any).globalRiskData || []} 
                                    />
                                </div>
                            )}
                            
                            <div 
                                className="prose prose-slate max-w-none text-slate-700 bg-white" 
                                dangerouslySetInnerHTML={{ __html: md.render(cleanedContent) }} 
                            />
                        </Section>
                    );
                })}
            </div>

            {result.disclaimer && (
                <div className="mt-8 pt-4 border-t border-slate-200 text-xs text-slate-500 italic">
                    {result.disclaimer}
                </div>
            )}
        </div>
    );
};

export default TextResultContent;
